<!-- components/StructuredData.vue -->
<template>
  <!-- No template content -->
</template>

<script>
export default {
  name: 'StructuredData',

  props: {
    type: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },

  watch: {
    '$route.path': {
      handler: 'updateStructuredData',
      immediate: true
    },
    type: {
      handler: 'updateStructuredData',
      immediate: true
    },
    data: {
      handler: 'updateStructuredData',
      deep: true,
      immediate: true
    }
  },

  beforeDestroy() {
    this.removeExisting()
  },

  methods: {
    updateStructuredData() {
      this.removeExisting()
      // Always inject for homepage or if data exists
      if (this.type === 'homepage' || Object.keys(this.data).length > 0) {
        this.injectStructuredData()
      }
    },

    injectStructuredData() {
      const jsonLd = this.generateSchema()
      if (!jsonLd) return

      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.className = 'structured-data-jsonld'
      script.textContent = JSON.stringify(jsonLd)
      document.head.appendChild(script)
    },

    generateSchema() {
      switch (this.type) {
        case 'listing':
          return this.getListingSchema(this.data)
        case 'homepage':
          return this.getHomepageSchema()
        case 'category':
          return this.getCategorySchema(this.data)
        case 'shop':
          return this.getShopSchema(this.data)
        default:
          return null
      }
    },

    removeExisting() {
      document
          .querySelectorAll('script.structured-data-jsonld')
          .forEach(el => el.remove())
    },

    getListingSchema(listing) {
      if (!listing) return null

      const images = listing.images?.map(image => image.image_url)
      return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": listing.name,
        "description": listing.description,
        "image": images,
        "sku": listing.id,
        "offers": {
          "@type": "Offer",
          "priceCurrency": 'KES',
          "price": listing.price,
          "itemCondition": listing.condition === 'new'
              ? 'https://schema.org/NewCondition'
              : 'https://schema.org/UsedCondition',
          "availability": listing.available
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          "url": `https://sokoni.ke/listings/${listing?.category?.slug}/${listing?.slug}/${listing?.id}`,
          "seller": {
            "@type": "Person",
            "name": listing.user?.full_name || ''
          }
        }
      }
    },

    getHomepageSchema() {
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Free classifieds in Kenya 🛍️ Post free classified ads.",
        "description": "Discover local deals on used items, services, vehicles, rentals, and more. Free Classifieds in Kenya. Sell online for free at sokoni.ke 🛍️ Post Ad in Kenya 🛍️ local classified ads. Best deals in your area. Free business website.",
        "url": window.location.href,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": window.location.origin
          }]
        }
      }
    },

    getCategorySchema(category) {
      if (!category) return null

      const descriptions = {
        'Vehicles': 'Buy and sell new or used vehicles in Kenya. Browse cars, motorcycles, trucks and more.',
        'Property': 'Find property listings in Kenya. Search houses, apartments, land and commercial spaces.',
        'Mobile Phones & Tablets': 'Shop for new and used mobile phones and tablets in Kenya.',
        'Electronics': 'Discover electronics for sale in Kenya, including TVs, laptops, cameras and more.',
        'Furniture & Appliances': 'Buy furniture and home appliances at great prices in Kenya.',
        'Health & Beauty': 'Explore health and beauty products, cosmetics, and wellness items in Kenya.',
        'Fashion': 'Find fashion items, clothing, shoes and accessories for men, women and kids.',
        'Sports, Arts & Outdoors': 'Browse sports equipment, art, and outdoor gear for sale in Kenya.',
        'Seeking Work CVs': 'Browse CVs of people seeking jobs across Kenya.',
        'Services': 'Find local services including repairs, tutoring, events, and more in Kenya.',
        'Jobs': 'Search for job openings across Kenya. Find full-time, part-time, and freelance work.',
        'Babies & Kids': 'Buy toys, clothing, and gear for babies and children in Kenya.',
        'Pets': 'Find pets and pet supplies including dogs, cats, birds and more in Kenya.',
        'Agriculture & Food': 'Browse agricultural products, seeds, food items and farm tools in Kenya.',
        'Commercial Equipment & Tools': 'Buy commercial and industrial tools and equipment in Kenya.',
        'Repair & Construction': 'Find repair and construction tools, equipment and services in Kenya.'
      }

      const fallbackDescription = `Browse listings for ${category.name} in Kenya.`
      const description = descriptions[category.name] || fallbackDescription

      return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": category.name,
        "description": description,
        "url": `${window.location.origin}/listings/categories/${category.slug}/${category.id}`,
        "image": category.image_url
      }
    },
    getShopSchema(shop) {
      console.log(shop)
      if (!shop || !shop.name || !shop.id) return null

      const sameAs = []
      if (shop.facebook_url) sameAs.push(shop.facebook_url)
      if (shop.twitter_url) sameAs.push(shop.twitter_url)
      if (shop.instagram_url) sameAs.push(shop.instagram_url)
      if (shop.linkedin_url) sameAs.push(shop.linkedin_url)
      if (shop.website_url && shop.website_url.startsWith('http')) sameAs.push(shop.website_url)

      const schema = {
        "@context": "https://schema.org",
        "@type": "Store",
        "name": shop.name,
        "image": shop.logo_url,
        "description": shop.description,
        "url": `https://sokoni.ke/shop/${shop.id}`,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": shop.location || "Kenya"
        },
        "telephone": shop.phone_number,
        "email": shop.email_address,
        ...(sameAs.length ? {sameAs} : {})
      }

      // Optional: Include product catalog if listings exist
      if (Array.isArray(shop.listings) && shop.listings.length) {
        schema.hasOfferCatalog = {
          "@type": "OfferCatalog",
          "name": "Shop Listings",
          "itemListElement": shop.listings.slice(0, 10).map(listing => ({
            "@type": "Offer",
            "itemOffered": this.getListingSchema(listing)
          }))
        }
      }

      return schema
    }
  }
}
</script>